import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ImageIcon } from "lucide-react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { Reveal, Eyebrow } from "./Reveal";
import { gallery, galleryCategories } from "@/config/site";

// Elegant "Image Coming Soon" placeholders shown when a category has no photos yet.
const PLACEHOLDER_COUNT = 6;

export const Gallery = () => {
  const [active, setActive] = useState("All");
  const [index, setIndex] = useState(-1);
  // Images load from /public/gallery/manifest.json if present, else from config `gallery`.
  // Upload photos to /public/gallery/ and list them in manifest.json — no code changes needed.
  const [items, setItems] = useState(gallery);

  useEffect(() => {
    fetch("/gallery/manifest.json")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) setItems(data);
      })
      .catch(() => {});
  }, []);

  const filtered = active === "All" ? items : items.filter((g) => g.category === active);
  const slides = filtered.map((g) => ({ src: g.img, description: g.category }));

  // Categories used for placeholder cards when there are no real photos yet.
  const placeholderCats =
    active === "All"
      ? galleryCategories.filter((c) => c !== "All")
      : Array(PLACEHOLDER_COUNT).fill(active);

  return (
    <section id="gallery" data-testid="gallery-section" className="relative bg-charcoal py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-2xl">
          <Reveal><Eyebrow dark>Our Work</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl leading-tight text-white">
              A glimpse into
              <span className="italic text-champagne"> the celebrations.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-4 text-sm text-white/50">
              Showcase imagery below — easily replaced later with your own event photographs.
            </p>
          </Reveal>
        </div>

        {/* Filters */}
        <Reveal delay={0.2} className="mt-10 flex flex-wrap gap-2.5">
          {galleryCategories.map((c) => (
            <button
              key={c}
              data-testid={`gallery-filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => setActive(c)}
              className={`rounded-full border px-4 py-2 text-xs tracking-wide transition-all duration-300 ${
                active === c
                  ? "border-champagne bg-champagne text-charcoal"
                  : "border-white/20 text-white/70 hover:border-champagne/60 hover:text-white"
              }`}
            >
              {c}
            </button>
          ))}
        </Reveal>

        {/* Masonry via CSS columns — real photos when available, elegant placeholders otherwise */}
        {filtered.length > 0 ? (
          <div className="mt-10 columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]">
            <AnimatePresence>
              {filtered.map((g, i) => (
                <motion.button
                  key={`${g.img}-${i}`}
                  layout
                  data-testid={`gallery-item-${i}`}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: (i % 3) * 0.05 }}
                  onClick={() => setIndex(i)}
                  className="group relative mb-4 block w-full break-inside-avoid overflow-hidden rounded-2xl"
                >
                  <img
                    src={g.img}
                    alt={`${g.category} event by Aarambh.KD`}
                    loading="lazy"
                    className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  <span className="absolute bottom-4 left-4 translate-y-3 text-sm tracking-[0.2em] uppercase text-champagne opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                    {g.category}
                  </span>
                </motion.button>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {placeholderCats.map((cat, i) => (
              <Reveal
                key={`ph-${cat}-${i}`}
                delay={(i % 3) * 0.06}
                data-testid={`gallery-placeholder-${i}`}
                className="relative flex aspect-[4/3] flex-col items-center justify-center overflow-hidden rounded-2xl border border-champagne/25 bg-gradient-to-br from-maroon/40 via-charcoal to-charcoal"
              >
                <div className="absolute inset-0 opacity-[0.15] bg-[radial-gradient(circle_at_30%_20%,#C9A227,transparent_60%)]" />
                <ImageIcon className="h-8 w-8 text-champagne/70" strokeWidth={1} />
                <p className="mt-4 font-display text-xl text-white/90">{cat}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.25em] text-champagne/70">
                  Image Coming Soon
                </p>
              </Reveal>
            ))}
          </div>
        )}
      </div>

      <Lightbox
        open={index >= 0}
        index={index < 0 ? 0 : index}
        close={() => setIndex(-1)}
        slides={slides}
      />
    </section>
  );
};
